import { Button } from '@up2stay/up2promo-ui';
import {
  cn,
  segmentedControlItemActiveClass,
  segmentedControlItemClass,
  segmentedControlTrackClass,
} from '@up2stay/up2promo-ui';
import { MonitorSmartphone, Moon, Sun } from 'lucide-react';

import { useSystemTheme } from '@/core/hooks/use-system-theme';
import { useDictionary } from '@/core/providers/dictionary-provider';
import {
  type ThemeSetting,
  usePreferencesStore,
} from '@/core/store/preferences.store';

type ThemeSwitcherProps = {
  variant?: 'default' | 'segmented';
  /** When false, segmented control omits the in-control title (use SettingsGroupLabel). */
  showLabel?: boolean;
};

export function ThemeSwitcher({
  variant = 'default',
  showLabel = true,
}: ThemeSwitcherProps) {
  const dict = useDictionary();
  const setting = usePreferencesStore((state) => state.themeSetting);
  const setThemeSetting = usePreferencesStore((state) => state.setThemeSetting);

  const systemTheme = useSystemTheme();
  const currentTheme: 'light' | 'dark' =
    setting === 'system' ? systemTheme : setting;
  // „System (hell/dunkel)“ names the OS scheme, not the active app theme, so
  // the option says what a click on it will do (DEV-537).
  const systemLabel =
    systemTheme === 'dark' ? dict.theme.systemDark : dict.theme.systemLight;

  const cycleSetting = () => {
    const next: ThemeSetting =
      setting === 'light' ? 'dark' : setting === 'dark' ? 'system' : 'light';
    setThemeSetting(next);
  };

  const isDark = currentTheme === 'dark';

  const label =
    setting === 'system'
      ? systemLabel
      : isDark
        ? dict.theme.darkMode
        : dict.theme.lightMode;

  if (variant === 'segmented') {
    const options: { value: ThemeSetting; icon: typeof Sun; label: string }[] =
      [
        { value: 'light', icon: Sun, label: dict.theme.lightMode },
        { value: 'system', icon: MonitorSmartphone, label: systemLabel },
        { value: 'dark', icon: Moon, label: dict.theme.darkMode },
      ];
    return (
      <div className={cn('flex flex-col', showLabel && 'gap-2')}>
        {showLabel ? (
          <span className="text-sm font-medium text-muted-foreground">
            {dict.appearance.title}
          </span>
        ) : null}
        <div
          className={cn(
            'inline-flex w-full',
            segmentedControlTrackClass,
            'rounded-md border border-border/60',
          )}
          role="group"
          aria-label={dict.theme.title}
        >
          {options.map((opt) => {
            const isSelected = setting === opt.value;
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setThemeSetting(opt.value)}
                aria-pressed={isSelected}
                aria-label={opt.label}
                className={cn(
                  segmentedControlItemClass,
                  'flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-medium',
                  isSelected && segmentedControlItemActiveClass,
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex flex-col">
        <span className="text-sm font-medium">{dict.theme.title}</span>
        <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
          {setting === 'system' ? (
            <MonitorSmartphone className="h-3.5 w-3.5" />
          ) : isDark ? (
            <Moon className="h-3.5 w-3.5" />
          ) : (
            <Sun className="h-3.5 w-3.5" />
          )}
          <span>{label}</span>
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={cycleSetting}
        aria-label={label}
        className="relative rounded-full"
      >
        {setting === 'system' ? (
          <MonitorSmartphone className="h-4 w-4" />
        ) : (
          <>
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </>
        )}
        <span className="sr-only">{label}</span>
      </Button>
    </div>
  );
}
